// ══════════════ CLIENT UI — cortège complet ══════════════
// Extrait de GerantClientUI.jsx (refacto étape ClientUI, dernier gros morceau, 08/07/2026).
// Regroupe volontairement 10 composants dans un seul fichier (découpage validé par Claire) :
// ClientOnboarding, CaniculeBannerClient, MigrationBanner, ClientUI, DemandeForm,
// ClientDashboardPresta, ClientPlanning, GuideProprietaire, GuideStep, GuideTip.
// Bloc entièrement contigu dans l'original. Règle du manuel : GuideProprietaire doit
// rester synchro avec ClientUI — les garder dans le même fichier protège cette règle.
// Feuille confirmée par contrôle anti-cycle exhaustif : toutes les dépendances externes
// sont déjà importées depuis leurs vraies sources. Closures internes (BadgeStatut,
// CarteNormale, CarteSurveillance, LBL, PIECES_VIGNETTE) voyagent avec leurs parents.
import React, { useState, useEffect } from 'react';
import { today, now, fd, feur, aD, iD, effSt, lundiDeISO, tp, JOUR_ASCII } from "../utils.js";
import { SUPA_URL, SUPA_KEY } from "../constants.js";
import { supaGet, currentAuthEmail, getSignedFactureUrl, SUPA_TOKEN } from "../supabase.js";
import { generateRapportPdf } from "../pdf-utils.js";
import { calcSejour, montantFactureActuel, regenererPDFDepuisFacture } from "./SecProp.jsx";
import { detaillerTachePrestation } from "./TaskForm.jsx";
import { TaxeSejourView, ClientRecapView, AvoirFacture, JustifsFacture } from "./Facturation.jsx";
import { SecStatutAppts } from "./SecStatut.jsx";
import { Ic } from "./Ic.jsx";
import { M } from "./M.jsx";
import { getRapportStructure } from './ErrorBoundary.jsx';
import { SimpleChat } from './SimpleChat.jsx';

// Affiché à la place de ClientUI si me.firstLogin===true.
// Force le changement de mot de passe avant tout accès à l'interface.
function ClientOnboarding({me, setClients, clients, authEmail}){
  const[newPwd,setNewPwd]=React.useState('');
  const[confirmPwd,setConfirmPwd]=React.useState('');
  const[err,setErr]=React.useState('');
  const[ok,setOk]=React.useState(false);
  const[loading,setLoading]=React.useState(false);
  const[showNew,setShowNew]=React.useState(false);

  const handleSetPassword=async()=>{
    setErr('');
    if(!newPwd.trim()||!confirmPwd.trim()){setErr('Veuillez remplir les deux champs.');return;}
    if(newPwd.length<8){setErr('Le mot de passe doit faire au moins 8 caractères.');return;}
    if(newPwd!==confirmPwd){setErr('Les deux mots de passe ne correspondent pas.');return;}
    setLoading(true);
    try{
      const updateRes=await fetch(`${SUPA_URL}/auth/v1/user`,{
        method:'PUT',
        headers:{'Content-Type':'application/json','apikey':SUPA_KEY,'Authorization':`Bearer ${SUPA_TOKEN}`},
        body:JSON.stringify({password:newPwd})
      });
      if(!updateRes.ok){
        const d=await updateRes.json();
        setErr(d?.msg||d?.message||'Erreur lors de la création du mot de passe.');
        setLoading(false);return;
      }
      // Retirer cet accès précis de la liste des onboardings en attente.
      const ae=(authEmail||'').trim().toLowerCase();
      const updated=clients.map(c=>{
        if(c.id!==me.id)return c;
        const nc={...c};
        if(Array.isArray(nc.firstLoginEmails)){
          nc.firstLoginEmails=nc.firstLoginEmails.filter(e=>(e||'').trim().toLowerCase()!==ae);
        }
        // Si c'est l'accès principal (ou ancien booléen global), on baisse aussi le flag historique.
        if(nc.firstLogin===true&&(!ae||(nc.email||'').trim().toLowerCase()===ae)){
          nc.firstLogin=false;
        }
        return nc;
      });
      setClients(updated);
      setOk(true);
    }catch(e){setErr('Erreur réseau. Réessayez.');setLoading(false);}
  };

  return(
    <div style={{minHeight:'60vh',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{maxWidth:420,width:'100%'}}>
        {/* Bienvenue */}
        <div style={{background:'linear-gradient(135deg,#ffe8e8,var(--wh))',borderLeft:'4px solid var(--pk)',borderRadius:12,padding:'20px 22px',marginBottom:24}}>
          <div className="D" style={{fontSize:24,marginBottom:6}}>Bienvenue {me?.name} 👋</div>
          <div style={{fontSize:13,color:'var(--si)',lineHeight:1.6}}>
            Votre espace propriétaire est prêt.<br/>
            Pour finaliser votre accès, veuillez choisir votre mot de passe personnel.
          </div>
        </div>
        {ok?(
          <div style={{background:'#e0fbe0',border:'1px solid #86efac',borderRadius:12,padding:'18px 20px',textAlign:'center'}}>
            <div style={{fontSize:22,marginBottom:8}}>✅</div>
            <div className="D" style={{fontSize:16,color:'#166534',marginBottom:6}}>Mot de passe créé !</div>
            <div style={{fontSize:12,color:'#166534',marginBottom:16,lineHeight:1.5}}>Votre compte est sécurisé. Reconnectez-vous avec votre nouveau mot de passe pour accéder à votre espace.</div>
            <button onClick={()=>{try{localStorage.removeItem('supa_session');}catch{}window.location.href=window.location.pathname;}} className="btn bor" style={{width:'100%',justifyContent:'center'}}>Se connecter →</button>
          </div>
        ):(
          <div style={{background:'var(--wh)',borderRadius:12,padding:'24px 22px',boxShadow:'0 4px 20px rgba(42,37,32,.08)',border:'1px solid var(--li)'}}>
            <div className="D" style={{fontSize:15,marginBottom:16,color:'var(--ink)'}}>🔒 Choisissez votre mot de passe</div>
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              <div>
                <label className="fl">Nouveau mot de passe <span style={{fontSize:10,color:'var(--si)'}}>(8 caractères min.)</span></label>
                <div style={{position:'relative'}}>
                  <input type={showNew?'text':'password'} value={newPwd} onChange={e=>{setNewPwd(e.target.value);setErr('');}} placeholder="Choisissez un mot de passe sécurisé" style={{paddingRight:40}} autoFocus/>
                  <button onClick={()=>setShowNew(v=>!v)} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--si)',padding:4}} tabIndex={-1}>{showNew?'🙈':'👁'}</button>
                </div>
              </div>
              {newPwd.length>0&&<div style={{height:4,borderRadius:2,background:'var(--li)',overflow:'hidden'}}>
                <div style={{height:'100%',borderRadius:2,transition:'width .3s,background .3s',width:`${Math.min(100,newPwd.length*8)}%`,background:newPwd.length<8?'#ef4444':newPwd.length<12?'#f59e0b':'#22c55e'}}/>
              </div>}
              <div>
                <label className="fl">Confirmer le mot de passe</label>
                <input type="password" value={confirmPwd} onChange={e=>{setConfirmPwd(e.target.value);setErr('');}} placeholder="Répétez votre mot de passe" onKeyDown={e=>{if(e.key==='Enter')handleSetPassword();}}/>
              </div>
              {err&&<div style={{background:'#fde0e0',border:'1px solid #f5b0b0',borderRadius:9,padding:'9px 13px',fontSize:12,color:'#8a1010'}}>⚠️ {err}</div>}
              <button onClick={handleSetPassword} disabled={loading} className="btn bor" style={{width:'100%',justifyContent:'center',padding:'11px 20px',fontSize:14,borderRadius:12}}>
                {loading?'Enregistrement…':'Créer mon mot de passe →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════ Bandeau "migration en cours" — interface propriétaires ══════════════
// Prévient les propriétaires qu'on consolide l'app et invite à signaler toute anomalie
// via la messagerie existante. Fermeture mémorisée (par appareil) pour ne pas être intrusif
// après la première lecture. Changer MIGRATION_BANNER_KEY pour le refaire réapparaître.

// Bandeau canicule propriétaires — apparaît si niveau orange ou rouge dans canicule7.
// Clé localStorage inclut le niveau pour re-afficher si le niveau monte.
function CaniculeBannerClient(){
  const[data,setData]=useState(null);
  const[dismissed,setDismissed]=useState(false);
  useEffect(()=>{
    supaGet('canicule7').then(d=>{
      if(d&&(d.niveau==='orange'||d.niveau==='rouge')){
        setData(d);
        const key=`canicule_banner_${d.niveau}_${(d.updatedAt||'').slice(0,10)}`;
        try{if(localStorage.getItem(key)==='1')setDismissed(true);}catch{}
      }
    }).catch(()=>{});
  },[]);
  if(!data||dismissed)return null;
  const isRouge=data.niveau==='rouge';
  const close=()=>{
    const key=`canicule_banner_${data.niveau}_${(data.updatedAt||'').slice(0,10)}`;
    try{localStorage.setItem(key,'1');}catch{}
    setDismissed(true);
  };
  return(<div className="card" style={{padding:'14px 18px',marginBottom:18,borderLeft:`4px solid ${isRouge?'#b71c1c':'#e65100'}`,background:isRouge?'#fff5f5':'#fff8f0'}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
      <div style={{flex:1}}>
        <div style={{fontWeight:700,fontSize:13,marginBottom:6,color:isRouge?'#b71c1c':'#e65100'}}>
          {isRouge?'🔴':'🟠'} Mesures canicule en vigueur
        </div>
        <div style={{fontSize:12,color:'var(--ink)',lineHeight:1.65}}>
          En raison des fortes chaleurs actuelles, nous avons mis en place des mesures de protection pour la santé de nos équipes, conformément à la réglementation en vigueur.<br/>
          Certains ménages pourront être <strong>légèrement rallongés</strong> ou <strong>reprogrammés en début de matinée</strong> afin d'éviter les heures les plus chaudes de la journée (12h–17h). Les interventions urgentes restent prioritaires.<br/>
          Nous veillons à la santé et à la sécurité de nos salariés tout en maintenant le niveau de qualité auquel vous êtes habitués. Merci pour votre compréhension.
        </div>
        <div style={{fontSize:11,color:'var(--si)',marginTop:8,fontStyle:'italic'}}>L'équipe La Conciergerie</div>
      </div>
      <button onClick={close} title="Fermer" style={{background:'none',border:'none',cursor:'pointer',fontSize:16,color:'var(--si)',padding:0,lineHeight:1,flexShrink:0}}>✕</button>
    </div>
  </div>);
}

function MigrationBanner(){
  return(<div style={{textAlign:'center',padding:'6px 0 2px',marginTop:8}}>
    <span style={{fontSize:10,color:'var(--si)'}}>Version bêta · Une anomalie ? Écrivez-nous via la messagerie.</span>
  </div>);
}

function ClientUI({sess,...P}){
  const{biens,req,setReq,pj,st,rep,setRep,msgs,setMsgs,clients,setClients,staff,gerants,controles,factures}=P;
  const me=clients.find(c=>c.id===sess.id);
  // Onboarding première connexion : forcer le changement de MDP.
  // Suivi par-email (firstLoginEmails) pour gérer un profil partagé (proprio + secrétaire),
  // avec rétrocompat sur l'ancien booléen global firstLogin.
  const authEmail=currentAuthEmail();
  const needsOnboarding=
    (Array.isArray(me?.firstLoginEmails)&&me.firstLoginEmails.map(e=>(e||'').trim().toLowerCase()).includes(authEmail))
    ||(me?.firstLogin===true&&(!authEmail||(me?.email||'').trim().toLowerCase()===authEmail));
  // Profil prestations (ex. Delphine Thomas) : interface orientée suivi opérationnel
  // (demandes/messagerie en avant, réclamations, planning), SANS récap locations ni taxe de séjour
  // (le propriétaire gère lui-même ses locations et encaisse en direct).
  const isPresta=me?.profilFacturation==='prestations';
  // ⚠️ RÈGLE DES HOOKS — NE PAS DÉPLACER CES useState SOUS LE `return` D'ONBOARDING CI-DESSOUS.
  // Un hook appelé après un return conditionnel n'est plus appelé au rendu suivant si la
  // condition change (fin de l'onboarding) → l'ordre des hooks diffère entre deux rendus →
  // crash React. isPresta est remonté avec eux car il sert de valeur initiale à `tab`.
  // Onglet par défaut selon le profil
  const[tab,setTab]=useState(isPresta?'dash':'recap');
  const[selR,setSelR]=useState(null);
  const[showF,setShowF]=useState(false);
  const[showGuide,setShowGuide]=useState(false);

  if(needsOnboarding){
    return <ClientOnboarding me={me} setClients={setClients} clients={clients} authEmail={authEmail}/>;
  }
  // Bandeau d'accueil personnalisé : un même profil propriétaire peut être consulté par plusieurs
  // personnes (compte principal + accès secondaires, ex. Daniel pour Delphine/My Sweet Homes).
  // On compare l'email connecté à celui du compte principal et à ceux des accès secondaires pour
  // choisir le bon prénom. Accès secondaire sans prénom renseigné → accueil neutre "Bonjour" (sans nom).
  const meEmail=(me?.email||'').trim().toLowerCase();
  const accesSecondaires=Array.isArray(me?.accesSecondaires)?me.accesSecondaires:[];
  const accesActuel=(authEmail&&authEmail!==meEmail)
    ?accesSecondaires.find(a=>(a?.email||'').trim().toLowerCase()===authEmail)
    :null;
  const helloName=accesActuel?(accesActuel.prenom||null):(me?.name||null);

  const myB=biens.filter(b=>b.cid===sess.id);
  // Nombre de biens "comptés" : on exclut les biens annexes (espaces extérieurs, communs, piscine
  // partagée...). Ils restent pris en compte partout ailleurs (ménages, montant prestations) mais
  // ne sont pas des logements, donc pas comptabilisés dans le nombre de biens affiché au propriétaire.
  const nbBiensComptes=myB.filter(b=>!b.bienAnnexe).length;
  const myQ=req.filter(r=>r.cid===sess.id);
  const myR=rep.filter(r=>myB.some(b=>b.id===r.bid)&&r.sub&&!r.hidClient);
  const allWorkers=[...(staff||[]),...((gerants||[]).map(g=>({...g,_gerant:true})))];
  const hideForClient=r=>{
    if(!window.confirm(`Retirer ce rapport de votre interface ?\n\nLa Conciergerie le conservera dans ses archives, mais il ne s'affichera plus chez vous. Cette action ne supprime pas définitivement le rapport.`))return;
    setRep(prev=>prev.map(x=>x.id===r.id?{...x,hidClient:true}:x));
    setSelR(null);
  };
  // isPresta est défini plus haut (règle des hooks : il sert de valeur initiale à `tab`).
  const planningOn=isPresta&&me?.planningClient===true;
  const suiviStatutOn=me?.suiviStatut===true;
  const nbReclam=myQ.filter(r=>r.type==='reclamation').length;
  const nbReclamOuvertes=myQ.filter(r=>r.type==='reclamation'&&r.st==='pending').length;
  const nbDemHorsReclam=myQ.filter(r=>r.type!=='reclamation'&&r.st==='pending').length;
  const ur=msgs.filter(m=>m.tid===`client:${sess.id}`&&!m.ru).length;
  const saveQ=d=>{
    const newReq={...d,id:'r'+Date.now()};
    setReq([...req,newReq]);
    setShowF(false);
  };
  // Factures émises pour ce propriétaire, triées par date décroissante
  const mesFactures=(factures||[]).filter(f=>f.cid===sess.id&&(f.statut==='emise'||f.statut==='payee'))
    .sort((a,b)=>new Date(b.dateEmission)-new Date(a.dateEmission));
  const nbFac=mesFactures.length;
  // Définition des onglets selon le profil
  const tabsDef=isPresta?[
    {id:'dash',el:<>📊 Tableau de bord</>},
    ...(planningOn?[{id:'plan',el:<>📅 Planning</>}]:[]),
    ...(suiviStatutOn?[{id:'statut',el:<>🏠 Statut</>}]:[]),
    {id:'rap',el:<><Ic n="rp"/>Rapports de ménage</>},
    {id:'msg',el:<><Ic n="ch"/>Messagerie{ur>0&&<span className="bc">{ur}</span>}</>},
    {id:'dem',el:<><Ic n="ib"/>Demandes{nbDemHorsReclam>0&&<span className="bc" style={{background:'var(--pk)'}}>{nbDemHorsReclam}</span>}</>},
    {id:'reclam',el:<>🛠 Réclamations{nbReclamOuvertes>0&&<span className="bc" style={{background:'#b91c1c'}}>{nbReclamOuvertes}</span>}</>},
    {id:'fac',el:<>🧾 Factures{nbFac>0&&<span className="bc" style={{background:'var(--pk)'}}>{nbFac}</span>}</>},
    {id:'bien',el:<><Ic n="h"/>Mes biens</>},
  ]:[
    {id:'recap',el:<>📊 Récap locations</>},
    {id:'ts',el:<>🏛️ Taxe de séjour</>},
    ...(suiviStatutOn?[{id:'statut',el:<>🏠 Statut</>}]:[]),
    {id:'fac',el:<>🧾 Factures{nbFac>0&&<span className="bc" style={{background:'var(--pk)'}}>{nbFac}</span>}</>},
    {id:'rap',el:<><Ic n="rp"/>Rapports de ménage</>},
    {id:'bien',el:<><Ic n="h"/>Mes biens</>},
    {id:'dem',el:<><Ic n="ib"/>Demandes{nbDemHorsReclam>0&&<span className="bc" style={{background:'var(--pk)'}}>{nbDemHorsReclam}</span>}</>},
    {id:'msg',el:<><Ic n="ch"/>Messagerie{ur>0&&<span className="bc">{ur}</span>}</>},
  ];
  return(<div className="client-neumo">
    <div className="card" style={{padding:'16px 18px',marginBottom:20}}><div className="D" style={{fontSize:24,marginBottom:2}}>Bonjour{helloName?` ${helloName}`:''}</div><div style={{fontSize:11,color:'var(--si)'}}>{me?.enseigne?`${me.enseigne} · `:''}{nbBiensComptes} bien{nbBiensComptes>1?'s':''} {isPresta?'suivis avec':'gérés par'} La Conciergerie</div></div>
    <CaniculeBannerClient/>
    <MigrationBanner/>
    <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:18}}>
      {tabsDef.map(t=><button key={t.id} className={`st ${tab===t.id?'on':''}`} onClick={()=>setTab(t.id)}>{t.el}</button>)}
    </div>
    {tab==='dash'&&isPresta&&<ClientDashboardPresta myB={myB} st={st} rep={rep} als={P.als||[]} feries={P.feries||[]} planningOn={planningOn} onVoirRapport={r=>{setTab('rap');setSelR(r);}} onVoirPlanning={()=>{if(planningOn)setTab('plan');}}/>}
    {tab==='recap'&&!isPresta&&<ClientRecapView sess={sess} sejours={P.sejours||[]} biens={biens} clients={clients} factures={P.factures||[]}/>}
    {tab==='ts'&&!isPresta&&<TaxeSejourView mode="client" sess={sess} sejours={P.sejours||[]} biens={biens} clients={clients}/>}
    {tab==='fac'&&(<div>
      {mesFactures.length===0?(
        <div className="empty">Aucune facture émise pour le moment.</div>
      ):(
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {mesFactures.map(f=>{
            const periodeAff=f.periodeLabel||'—';
            const dateEm=f.dateEmission?new Date(f.dateEmission).toLocaleDateString('fr-FR'):'—';
            const datePay=f.datePaiement?new Date(f.datePaiement).toLocaleDateString('fr-FR'):null;
            const payee=f.statut==='payee';
            return(<div key={f.id} className="card" style={{padding:'14px 18px',display:'flex',alignItems:'center',gap:16,flexWrap:'wrap',borderLeft:`4px solid ${payee?'var(--ok)':'var(--pk)'}`}}>
              <div style={{fontSize:28}}>{payee?'✅':'🧾'}</div>
              <div style={{flex:1,minWidth:180}}>
                <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                  <span style={{fontWeight:700,fontSize:15,color:payee?'var(--ok)':'var(--pk)'}}>{f.numeroDefinitif}</span>
                  {f.type==='externe'&&<span style={{background:'#f0e6ff',color:'#5a2d82',fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:999}}>📎 Facture externe</span>}
                  {payee&&<span style={{background:'#e1f7ee',color:'var(--ok)',fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:999}}>Réglée</span>}
                </div>
                <div style={{fontSize:13,color:'var(--si)',marginTop:2}}>{periodeAff}</div>
                <div style={{fontSize:11,color:'var(--si)',marginTop:1}}>Émise le {dateEm}{datePay&&` · Réglée le ${datePay}`}</div>
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:11,color:'var(--si)',textTransform:'uppercase',letterSpacing:'.05em'}}>Total TTC</div>
                <div style={{fontWeight:800,fontSize:20,color:payee?'var(--ok)':'var(--pk)'}}>{(Math.round(montantFactureActuel(f,P.sejours||[],biens)*100)/100).toFixed(2).replace('.',',')+'  €'}</div>
              </div>
              <button onClick={()=>{
                if(f.type==='externe'){
                  // Facture externe : ouvrir le PDF depuis le Storage (URL signée)
                  getSignedFactureUrl(f.pdfPath,3600)
                    .then(url=>window.open(url,'_blank'))
                    .catch(e=>alert('Impossible d\'ouvrir le PDF : '+e.message));
                  return;
                }
                const client=clients.find(c=>c.id===sess.id);
                const lignesIds=new Set(f.lignesIds||[]);
                const lignesF=(P.sejours||[])
                  .filter(s=>lignesIds.has(s.id))
                  .map(s=>({s,bien:biens.find(b=>b.id===s.bid)||{name:'',tarifLinge2p:0,tarifLinge1p:0},calc:calcSejour(s,biens.find(b=>b.id===s.bid)||{},client)}))
                  .filter(l=>l.bien);
                if(!(f.snapshotLignes&&f.snapshotLignes.length)&&!lignesF.length){alert('Impossible de régénérer : les séjours associés sont introuvables.');return;}
                regenererPDFDepuisFacture(f,client,lignesF)
                  .catch(e=>alert('Erreur PDF : '+e.message));
              }} className="btn bpk sm">⬇ PDF</button>
              {f.type!=='externe'&&(f.justifs||[]).length>0&&<JustifsFacture facture={f} mode="client"/>}
              {f.type!=='externe'&&f.avoir&&<AvoirFacture facture={f} mode="client"/>}
            </div>);
          })}
        </div>
      )}
    </div>)}
    {tab==='rap'&&(<div>
      {myR.length===0?<div className="empty">Aucun rapport</div>:<div style={{display:'flex',flexDirection:'column',gap:8}}>{myR.sort((a,b)=>b.date.localeCompare(a.date)).map(r=>{const b=biens.find(x=>x.id===r.bid);const struct=getRapportStructure(b);const total=struct.items.length;const ok=Object.values(r.cks||{}).filter(Boolean).length;const worker=allWorkers.find(s=>s.id===r.sid);const ctrl=(controles||[]).find(c=>c.repId===r.id);return(<div key={r.id} className="card" style={{padding:'11px 14px',borderLeft:`3px solid ${ok===total?'var(--ok)':'var(--ye)'}`}}><div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}><div><div style={{display:'flex',gap:5,marginBottom:4,flexWrap:'wrap'}}><span className="pill pok">✅ Ménage validé</span><span style={{fontWeight:600,fontSize:12}}>{b?.name}</span><span style={{fontSize:11,color:'var(--si)'}}>{fd(r.date)}</span></div><div style={{fontSize:12}}>{ok}/{total} points · {Object.keys(r.gp||{}).filter(k=>r.gp[k]).length} photos</div></div><div style={{display:'flex',gap:5,flexShrink:0,flexWrap:'wrap'}}><button onClick={()=>setSelR(r)} className="btn bgh sm"><Ic n="ey" c="w-3.5 h-3.5"/> Voir</button><button onClick={()=>generateRapportPdf({rapport:r,bien:b,worker,struct,controle:ctrl,hideWorker:true})} className="btn bor sm" title="Télécharger le rapport en PDF">📄 PDF</button><button onClick={()=>hideForClient(r)} className="btn bgh sm" title="Retirer ce rapport de votre interface" style={{color:'var(--dg)'}}>🗑</button></div></div></div>);})}</div>}
      {selR&&(()=>{const b=biens.find(x=>x.id===selR.bid);const struct=getRapportStructure(b);const worker=allWorkers.find(s=>s.id===selR.sid);const ctrl=(controles||[]).find(c=>c.repId===selR.id);return<M onC={()=>setSelR(null)} title="Rapport de ménage" col="var(--pk)" wide ch={<div style={{display:'flex',flexDirection:'column',gap:14}}>
        <div style={{background:'var(--cr)',borderRadius:9,padding:'10px 13px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
          <div><div className="D" style={{fontSize:14}}>{b?.name}</div><div style={{fontSize:11,color:'var(--si)'}}>{fd(selR.date)}</div></div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
            <button onClick={()=>generateRapportPdf({rapport:selR,bien:b,worker,struct,controle:ctrl,hideWorker:true})} className="btn bor sm">📄 Télécharger PDF</button>
            <button onClick={()=>hideForClient(selR)} className="btn bgh sm" style={{color:'var(--dg)'}}>🗑 Retirer</button>
          </div>
        </div>
        {struct.sections.map(sec=>(<div key={sec.key}>
          <div style={{fontSize:struct.useNew?12:10,textTransform:struct.useNew?'none':'uppercase',letterSpacing:struct.useNew?'0':'.08em',fontWeight:700,color:struct.useNew?'var(--or)':'var(--si)',marginBottom:5}}>{sec.title}</div>
          <div style={{display:'flex',flexDirection:'column',gap:3}}>
            {sec.items.map(item=>(<div key={item.id} style={{fontSize:13,color:selR.cks?.[item.id]?'var(--ok)':'var(--dg)'}}>{selR.cks?.[item.id]?'✅':'❌'} {item.lb}{(item.photo||item.pk)&&selR.pk?.[item.id]?' 📷':''}</div>))}
          </div>
        </div>))}
        {/* Suppléments réalisés — côté propriétaire : libellés uniquement, sans montant (pour éviter
           toute confusion HT/TTC avec la facture). Quantité conservée pour les items numériques. */}
        {(()=>{
          const bf=Array.isArray(b?.bf)?b.bf:[];
          if(!b?.bfOn||bf.length===0)return null;
          const actifs=bf.map(field=>{
            const v=(selR.bill||{})[field.id];
            if(field.t==='n'){const qty=Number(v)||0;return{...field,qty,actif:qty>0};}
            return{...field,qty:v===true?1:0,actif:v===true};
          }).filter(l=>l.actif);
          if(actifs.length===0)return null;
          return(<div style={{padding:'10px 12px',background:'#fff4e8',borderRadius:8,border:'1.5px solid var(--or)'}}>
            <strong style={{fontSize:13,color:'var(--or)',display:'block',marginBottom:8}}>Suppléments réalisés</strong>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              {actifs.map(l=>(<div key={l.id} style={{display:'flex',alignItems:'center',fontSize:12,padding:'3px 0',borderBottom:'1px dashed var(--li)'}}>
                <span style={{marginRight:6}}>{l.em||'💰'}</span>{l.lb}{l.t==='n'?` × ${l.qty}`:''}
              </div>))}
            </div>
          </div>);
        })()}
        {struct.photoItems.length>0&&<div>
          <div className="D" style={{fontSize:15,marginBottom:8}}>Photos des points</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:8}}>
            {struct.photoItems.map(it=>(<div key={it.id}>
              <div style={{fontSize:11,fontWeight:600,marginBottom:3}}>{it._rmEmoji?it._rmEmoji+' ':''}{it._photoLabel||it.pl||it.lb}</div>
              {selR.pk?.[it.id]?<img src={selR.pk[it.id]} alt={it.lb} style={{width:'100%',borderRadius:7,aspectRatio:'4/3',objectFit:'cover'}}/>:<div style={{background:'#f5f5f5',borderRadius:7,height:70,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,color:'var(--si)'}}>Non fournie</div>}
            </div>))}
          </div>
        </div>}
        {struct.genPhotos.length>0&&<div>
          <div className="D" style={{fontSize:15,marginBottom:8}}>Photos générales</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:8}}>
            {struct.genPhotos.map(p=>(<div key={p.id}>
              <div style={{fontSize:11,fontWeight:600,marginBottom:3}}>{p._rmEmoji?p._rmEmoji+' ':''}{p.l}</div>
              {selR.gp?.[p.id]?<img src={selR.gp[p.id]} alt={p.l} style={{width:'100%',borderRadius:7,aspectRatio:'4/3',objectFit:'cover'}}/>:<div style={{background:'#f5f5f5',borderRadius:7,height:70,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,color:'var(--si)'}}>Non fournie</div>}
            </div>))}
          </div>
        </div>}
      </div>}/>;})()}
    </div>)}
    {tab==='bien'&&<div style={{display:'flex',flexDirection:'column',gap:10}}>{myB.map(b=><div key={b.id} className="card" style={{padding:'13px 16px'}}><div style={{height:3,borderRadius:999,background:'var(--pk)',marginBottom:10}}/><div className="D" style={{fontSize:16,marginBottom:2}}>{b.name}</div><div style={{fontSize:11,color:'var(--si)',marginBottom:6}}>{b.addr}</div>{b.notes&&<div style={{fontSize:12,lineHeight:1.5,color:'var(--si)'}}>{b.notes}</div>}</div>)}</div>}
    {tab==='dem'&&(()=>{const demandes=myQ.filter(r=>r.type!=='reclamation');return(<div>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}><div className="D" style={{fontSize:18}}>Mes demandes</div><button onClick={()=>setShowF(true)} className="btn bpk sm"><Ic n="plus"/>Demande</button></div>
      {demandes.length===0?<div className="empty">Aucune demande</div>:<div style={{display:'flex',flexDirection:'column',gap:8}}>{demandes.map(r=>{const b=biens.find(x=>x.id===r.bid);return(<div key={r.id} className="card" style={{padding:'11px 14px'}}><div style={{display:'flex',gap:5,marginBottom:4,flexWrap:'wrap'}}><span className={`pill ${r.st==='pending'?'pnd':r.st==='approved'?'pok':'pdg'}`}>{r.st==='pending'?'En attente':r.st==='approved'?'Acceptée':'Refusée'}</span><span style={{fontSize:11,background:'var(--cr)',padding:'2px 6px',borderRadius:999}}>{r.type}</span></div><div className="D" style={{fontSize:13}}>{b?.name}</div><div style={{fontSize:11,color:'var(--si)'}}>{fd(r.rf)}</div><div style={{fontSize:12,marginTop:3}}>{r.desc}</div></div>);})}</div>}
      {showF===true&&<M onC={()=>setShowF(false)} title="Nouvelle demande" col="var(--pk)" ch={<DemandeForm myB={myB} cid={sess.id} onSave={saveQ} onC={()=>setShowF(false)} isPresta={isPresta}/>}/>}
    </div>);})()}

    {/* ── RÉCLAMATIONS (clients prestations) ── */}
    {tab==='reclam'&&(()=>{const reclams=myQ.filter(r=>r.type==='reclamation').sort((a,b)=>(b.ca||'').localeCompare(a.ca||'')||(b.rf||'').localeCompare(a.rf||''));return(<div>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:8,flexWrap:'wrap',gap:8}}><div className="D" style={{fontSize:18}}>🛠 Mes réclamations</div><button onClick={()=>setShowF('reclam')} className="btn sm" style={{background:'#b91c1c',color:'#fff'}}><Ic n="plus"/>Réclamation</button></div>
      <div style={{fontSize:12,color:'var(--si)',marginBottom:12,lineHeight:1.5}}>Signalez un problème constaté (qualité d'une intervention, oubli, dégradation, anomalie…). L'équipe La Conciergerie est notifiée et vous répond directement.</div>
      {reclams.length===0?<div className="empty">Aucune réclamation. Tant mieux 🙂</div>:<div style={{display:'flex',flexDirection:'column',gap:8}}>{reclams.map(r=>{const b=biens.find(x=>x.id===r.bid);return(<div key={r.id} className="card" style={{padding:'11px 14px',borderLeft:`3px solid ${r.st==='pending'?'#b91c1c':r.st==='approved'?'var(--ok)':'var(--si)'}`}}><div style={{display:'flex',gap:5,marginBottom:4,flexWrap:'wrap',alignItems:'center'}}><span className={`pill ${r.st==='pending'?'pnd':r.st==='approved'?'pok':'pdg'}`}>{r.st==='pending'?'En cours de traitement':r.st==='approved'?'Traitée':'Clôturée'}</span><span style={{fontSize:11,background:'#fde8e8',color:'#b91c1c',padding:'2px 7px',borderRadius:999,fontWeight:700}}>🛠 Réclamation</span></div><div className="D" style={{fontSize:13}}>{b?.name}</div><div style={{fontSize:11,color:'var(--si)'}}>Signalée le {fd(r.rf)}</div><div style={{fontSize:12,marginTop:3}}>{r.desc}</div></div>);})}</div>}
      {showF==='reclam'&&<M onC={()=>setShowF(false)} title="Nouvelle réclamation" col="#b91c1c" ch={<DemandeForm myB={myB} cid={sess.id} onSave={saveQ} onC={()=>setShowF(false)} isPresta={isPresta} defaultType="reclamation"/>}/>}
    </div>);})()}

    {/* ── PLANNING (clients prestations, si activé) ── */}
    {tab==='plan'&&planningOn&&<ClientPlanning myB={myB} st={st} als={P.als||[]}/>}

    {/* ── STATUT DES APPARTEMENTS (si suiviStatut activé sur la fiche proprio) ── */}
    {tab==='statut'&&suiviStatutOn&&<SecStatutAppts
      biens={biens}
      clients={clients}
      sejours={P.sejours||[]}
      rep={P.rep||[]}
      controles={P.controles||[]}
      statutAppts={P.statutAppts||{}}
      setStatutAppts={P.setStatutAppts||null}
      sess={sess}
      st={P.st||[]}
      als={P.als||[]}
      filPartage={P.filPartage||[]}
      setFilPartage={P.setFilPartage||null}
      filProprio={P.filProprio||[]}
      setFilProprio={P.setFilProprio||null}
      commentaires={null}
      onNav={null}
    />}

    {tab==='msg'&&<SimpleChat sess={sess} msgs={msgs} setMsgs={setMsgs} accent="var(--pk)" senderName={helloName||me?.name||null}/>}

    {/* ── Bouton aide flottant ── */}
    <button onClick={()=>setShowGuide(true)} title="Guide d'utilisation" style={{position:'fixed',bottom:22,right:22,width:44,height:44,borderRadius:'50%',background:'#b03060',color:'#fff',border:'3px solid #fff',fontSize:22,fontWeight:700,cursor:'pointer',zIndex:1200,boxShadow:'0 3px 14px rgba(0,0,0,.30)',display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>?</button>

    {/* ── Modale guide propriétaire ── */}
    {showGuide&&<M onC={()=>setShowGuide(false)} title="Guide de votre espace" col="var(--pk)" wide ch={<GuideProprietaire isPresta={isPresta} planningOn={planningOn}/>}/>}
  </div>);
}

// ── Formulaire demande propriétaire (enrichi) ──
function DemandeForm({myB,cid,onSave,onC,isPresta,defaultType}){
  const[bid,setBid]=useState(myB[0]?.id||'');
  const[type,setType]=useState(defaultType||'menage');
  const[rf,setRf]=useState(tp(3));
  const[desc,setDesc]=useState('');
  // Ménage : créneau
  const[creneau,setCreneau]=useState('any'); // 'any' | 'from' | 'before'
  const[heureDebut,setHeureDebut]=useState('10:00');
  const[heureFin,setHeureFin]=useState('16:00');
  // Check-in : coordonnées locataire
  const[locNom,setLocNom]=useState('');
  const[locTel,setLocTel]=useState('');
  const[locEmail,setLocEmail]=useState('');

  const isMenage=type==='menage';
  const isCheckin=type==='checkin';

  const handleSend=()=>{
    if(!bid)return;
    const base={cid,bid,rf,type,desc,st:'pending',ca:now()};
    if(isMenage){
      base.creneau=creneau;
      if(creneau==='from')base.heureDebut=heureDebut;
      if(creneau==='before')base.heureFin=heureFin;
    }
    if(isCheckin){
      base.locataire={nom:locNom,tel:locTel,email:locEmail};
    }
    onSave(base);
  };

  const LBL=({children})=><label className="fl" style={{marginBottom:4,display:'block'}}>{children}</label>;

  return(<div style={{display:'flex',flexDirection:'column',gap:12}}>
    <div><LBL>Bien</LBL>
      <select value={bid} onChange={e=>setBid(e.target.value)}>
        {myB.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
    </div>
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
      <div><LBL>Type</LBL>
        <select value={type} onChange={e=>setType(e.target.value)}>
          <option value="menage">Ménage</option>
          <option value="checkin">Check-in</option>
          <option value="travaux">Travaux</option>
          <option value="linge">Linge</option>
          {isPresta&&<option value="reclamation">Réclamation</option>}
          <option value="autre">Autre</option>
        </select>
      </div>
      <div><LBL>{isCheckin?'Date d\'arrivée':type==='reclamation'?'Date du problème':'Date souhaitée'}</LBL>
        <input type="date" value={rf} onChange={e=>setRf(e.target.value)}/>
      </div>
    </div>

    {/* Créneaux ménage */}
    {isMenage&&<div>
      <LBL>Créneau horaire</LBL>
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
        {[['any','N\'importe quand'],['from','Disponible à partir de…'],['before','À faire avant…']].map(([v,l])=>
          <button key={v} onClick={()=>setCreneau(v)} type="button"
            style={{fontSize:11,padding:'5px 11px',borderRadius:20,border:`1.5px solid ${creneau===v?'var(--pk)':'#ddd'}`,background:creneau===v?'var(--pk)':'#fff',color:creneau===v?'#fff':'#666',cursor:'pointer',fontWeight:creneau===v?700:400}}>
            {l}
          </button>
        )}
      </div>
      {creneau==='from'&&<div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:12,color:'var(--si)'}}>Disponible à partir de</span>
        <input type="time" value={heureDebut} onChange={e=>setHeureDebut(e.target.value)} style={{width:110}}/>
      </div>}
      {creneau==='before'&&<div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:12,color:'var(--si)'}}>À faire avant</span>
        <input type="time" value={heureFin} onChange={e=>setHeureFin(e.target.value)} style={{width:110}}/>
      </div>}
    </div>}

    {/* Coordonnées locataire check-in */}
    {isCheckin&&<div style={{border:'1px solid #e8ddd4',borderRadius:10,padding:'12px',background:'var(--cr)'}}>
      <div style={{fontSize:11,fontWeight:700,color:'var(--si)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:8}}>Coordonnées du locataire</div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
        <div><LBL>Nom</LBL><input type="text" placeholder="Jean Dupont" value={locNom} onChange={e=>setLocNom(e.target.value)}/></div>
        <div><LBL>Téléphone</LBL><input type="tel" placeholder="+33 6 00 00 00 00" value={locTel} onChange={e=>setLocTel(e.target.value)}/></div>
      </div>
      <div><LBL>Email</LBL><input type="email" placeholder="jean@example.com" value={locEmail} onChange={e=>setLocEmail(e.target.value)}/></div>
    </div>}

    <div><LBL>Description</LBL><textarea rows="3" value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Précisions éventuelles…"/></div>
    <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
      <button onClick={onC} className="btn bgh">Annuler</button>
      <button onClick={handleSend} className="btn bpk">Envoyer</button>
    </div>
  </div>);
}

// ── Tableau de bord propriétaire (clients PRESTATIONS uniquement) ──
// Accueil orienté suivi opérationnel de la semaine en cours :
//  1. Ménages de la semaine (bloc dominant) : prochain créneau mis en avant, puis liste à venir / faits.
//  2. Donut check-in de la semaine (indicateur neumorphism, chiffre concret toujours pertinent).
//  3. Montant prestations de la semaine (provisoire) — calculé via le moteur Morceau 2
//     (detaillerTachePrestation), source unique de vérité, pour ne pas diverger de la facture.
//  4. 3 derniers rapports de ménage avec photo (tous biens confondus) — preuve visuelle.
// JAMAIS d'intervenant affiché. Lecture seule.
function ClientDashboardPresta({myB,st,rep,als,feries,planningOn,onVoirRapport,onVoirPlanning}){
  const bIds=new Set(myB.map(b=>b.id));
  const bienById=id=>myB.find(b=>b.id===id);
  const r2=n=>Math.round((Number(n)+Number.EPSILON)*100)/100;
  const today=iD(new Date());
  const lundi=lundiDeISO(today);
  const dimanche=iD(aD(new Date(lundi+'T12:00:00'),6));
  // Semaine précédente (pour la tendance check-in).
  const lundiPrec=iD(aD(new Date(lundi+'T12:00:00'),-7));
  const dimanchePrec=iD(aD(new Date(lundi+'T12:00:00'),-1));
  const hhmm=t=>`${String(t.sh).padStart(2,'0')}h${String(t.sm).padStart(2,'0')}`;
  const labelSem=`${new Date(lundi+'T12:00:00').toLocaleDateString('fr-FR',{day:'numeric',month:'long'})} → ${new Date(dimanche+'T12:00:00').toLocaleDateString('fr-FR',{day:'numeric',month:'long'})}`;

  // Tous les ménages de la semaine (full/quick), triés par date puis heure — sert au compteur global.
  const menages=(st||[])
    .filter(t=>bIds.has(t.bid)&&(t.type==='full'||t.type==='quick')&&t.st!=='cancelled'&&t.date>=lundi&&t.date<=dimanche)
    .sort((a,b)=>a.date.localeCompare(b.date)||(a.sh-b.sh)||(a.sm-b.sm));

  // ── Logique "tableau de bord" : on affiche les PROCHAINS créneaux à venir, quelle que soit la
  // semaine. Un ménage du jour reste tant que son heure n'est pas passée ; ensuite on enchaîne
  // naturellement sur les jours (et semaines) suivants. On garde le prochain + 2 suivants.
  const now=new Date();
  const minutesNow=now.getHours()*60+now.getMinutes();
  const aVenir=(st||[])
    .filter(t=>{
      if(!bIds.has(t.bid)||t.st==='cancelled'||t.st==='done')return false;
      if(t.type!=='full'&&t.type!=='quick')return false;
      if(!t.sid)return false; // non affecté ou à risque : pas un créneau confirmé, on ne le montre pas au proprio
      if(t.date>today)return true;
      if(t.date<today)return false;
      return (t.sh*60+(t.sm||0))>=minutesNow; // aujourd'hui : seulement les créneaux pas encore passés
    })
    .sort((a,b)=>a.date.localeCompare(b.date)||(a.sh-b.sh)||(a.sm-b.sm));
  const prochain=aVenir[0]||null;
  const suivants=aVenir.slice(1,3); // les 2 suivants

  // Check-in semaine en cours + semaine précédente (pour la tendance).
  const nbCheckin=(st||[]).filter(t=>bIds.has(t.bid)&&t.type==='checkin'&&t.st!=='cancelled'&&t.date>=lundi&&t.date<=dimanche).length;
  const nbCheckinPrec=(st||[]).filter(t=>bIds.has(t.bid)&&t.type==='checkin'&&t.st!=='cancelled'&&t.date>=lundiPrec&&t.date<=dimanchePrec).length;
  const deltaCheckin=nbCheckin-nbCheckinPrec;

  // Montant prestations de la semaine (provisoire, TTC) via le moteur Morceau 2.
  // detaillerTachePrestation renvoie des montants HT ; le TTC = HT × 1,2 (TVA 20%),
  // exactement comme l'aperçu de facture. On agrège ménages (par jourSortie/date) + check-in + majorations.
  const montantSemaine=(()=>{
    let ht=0;
    const tachesSem=(st||[]).filter(t=>{
      if(!bIds.has(t.bid)||t.st==='cancelled')return false;
      if(t.risque&&!t.sid)return false; // tâche à risque = doublon de surveillance, ne pas compter (sinon montant gonflé)
      if(t.binomeRole==='binome')return false; // binôme : même ménage que le titulaire, déjà compté une fois (même règle que la facture / caTask)
      const jour=t.jourSortie||t.date;
      return jour>=lundi&&jour<=dimanche;
    });
    tachesSem.forEach(t=>{
      const det=detaillerTachePrestation(t,bienById(t.bid),rep,als,feries);
      if(!det)return;
      (det.lignes||[]).forEach(l=>{ht+=r2((Number(l.q)||0)*(Number(l.pu)||0));});
      ht+=Number(det.majoration)||0;
    });
    return r2(ht*1.2);
  })();

  // 3 derniers rapports avec au moins une photo (tous biens confondus, hors masqués client).
  // Liste blanche des pièces affichables en vignette d'accueil : on ne montre QUE salon / chambre
  // (et séjour / salle à manger). On exclut volontairement SdB, WC, cuisine — moins flatteurs côté proprio.
  const PIECES_VIGNETTE=/(salon|chambre|s[ée]jour|salle\s*[àa]\s*manger)/i;
  // Photo "présentable" d'un rapport : première photo générale OU de point dont la pièce est en liste blanche.
  // gp / pk sont indexés par id de photo ; on remonte au nom de pièce via la structure du bien.
  const photoVignette=r=>{
    const b=bienById(r.bid);if(!b)return null;
    const struct=getRapportStructure(b);
    // Photos générales (clé = id, libellé pièce = _rmName)
    for(const p of (struct.genPhotos||[])){
      const url=r.gp?.[p.id];
      if(url&&PIECES_VIGNETTE.test(p._rmName||p.l||''))return url;
    }
    // Photos de points (clé = id, libellé pièce = _rmName)
    for(const it of (struct.photoItems||[])){
      const url=r.pk?.[it.id];
      if(url&&PIECES_VIGNETTE.test(it._rmName||it.lb||''))return url;
    }
    return null; // aucune photo salon/chambre dans ce rapport
  };
  const nbPhotos=r=>Object.values(r.gp||{}).filter(Boolean).length+Object.values(r.pk||{}).filter(Boolean).length;
  // 3 derniers rapports AYANT une photo présentable (salon/chambre). On ignore les autres.
  const derniersRapports=(rep||[])
    .filter(r=>bIds.has(r.bid)&&r.sub&&!r.hidClient)
    .sort((a,b)=>b.date.localeCompare(a.date))
    .map(r=>({r,img:photoVignette(r)}))
    .filter(x=>x.img)
    .slice(0,3);

  const labelJour=t=>{
    if(t.date===today)return"Aujourd'hui";
    const demain=iD(aD(new Date(today+'T12:00:00'),1));
    if(t.date===demain)return'Demain';
    const d=new Date(t.date+'T12:00:00');
    const i=(d.getDay()+6)%7;
    // Au-delà de 6 jours, on précise le jour du mois pour lever toute ambiguïté (semaine suivante).
    const joursEcart=Math.round((d-new Date(today+'T12:00:00'))/86400000);
    if(joursEcart>6)return `${JOUR_ASCII[i]} ${d.getDate()}/${d.getMonth()+1}`;
    return JOUR_ASCII[i];
  };

  return(<div>
    <div style={{fontSize:12,fontWeight:700,color:'var(--si)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:14}}>Tableau de bord · semaine du {labelSem}</div>

    {/* Ligne 1 : ménages (dominant) + donut check-in + montant */}
    <div style={{display:'flex',gap:16,flexWrap:'wrap',marginBottom:16}}>
      <div className="card" style={{flex:2,minWidth:300,padding:20}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:14,flexWrap:'wrap',gap:6}}>
          <div className="D" style={{fontSize:17}}>🧹 Prochains ménages</div>
          <div style={{fontSize:12,color:'var(--si)'}}>{menages.length} cette semaine</div>
        </div>
        {!prochain?<div className="empty">Aucun ménage à venir pour le moment.</div>:<>
          {(()=>{const b=bienById(prochain.bid);return(
            <div style={{borderRadius:14,boxShadow:'inset 4px 4px 9px var(--nsh1),inset -4px -4px 9px var(--nsh2)',padding:'16px 18px',marginBottom:14,display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
              <div style={{textAlign:'center',minWidth:60}}>
                <div style={{fontSize:10,color:'var(--si)',textTransform:'uppercase'}}>Prochain</div>
                <div style={{fontSize:20,fontWeight:800,color:'#0f6e56'}}>{hhmm(prochain)}</div>
                <div style={{fontSize:10,color:'var(--si)'}}>{labelJour(prochain).toLowerCase()}</div>
              </div>
              <div style={{flex:1,minWidth:140}}>
                <div style={{fontWeight:700,fontSize:15}}>{b?.name||'Logement'}</div>
                <div style={{fontSize:12,color:'var(--si)'}}>{prochain.type==='quick'?'⚡ Ménage rapide':'🧹 Ménage complet'}</div>
              </div>
            </div>);})()}
          {suivants.length>0&&<div style={{display:'flex',flexDirection:'column',gap:8}}>
            {suivants.map(t=>{const b=bienById(t.bid);return(
              <div key={t.id} style={{display:'flex',alignItems:'center',gap:12,padding:'8px 4px',borderTop:'1px solid var(--li)'}}>
                <div style={{fontWeight:800,color:'#0f6e56',minWidth:96,fontSize:13}}>{labelJour(t)} · {hhmm(t)}</div>
                <div style={{flex:1,minWidth:120,fontSize:13}}><strong>{b?.name||'Logement'}</strong><div style={{fontSize:11,color:'var(--si)'}}>{t.type==='quick'?'Ménage rapide':'Ménage complet'}</div></div>
              </div>);})}
          </div>}
          {planningOn&&<button onClick={onVoirPlanning} className="btn bgh sm" style={{marginTop:14,fontSize:11}}>Voir le planning complet →</button>}
        </>}
      </div>

      <div style={{flex:1,minWidth:180,display:'flex',flexDirection:'column',gap:16}}>
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:10}}>
          <div style={{width:170,height:170,borderRadius:'50%',background:'var(--nbg)',boxShadow:'11px 11px 26px var(--nsh1),-11px -11px 26px var(--nsh2)',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div style={{width:122,height:122,borderRadius:'50%',background:'var(--nbg)',boxShadow:'inset 6px 6px 14px var(--nsh1),inset -6px -6px 14px var(--nsh2)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',textAlign:'center'}}>
              <div style={{width:30,height:2,background:'#39c98a',marginBottom:7,borderRadius:2}}/>
              <div className="D" style={{fontSize:34,color:'#2f3e45',lineHeight:1}}>{nbCheckin}</div>
              <div style={{fontSize:11,color:'#5f6e75',lineHeight:1.25,marginTop:3}}>Check-in<br/>cette semaine</div>
              <div style={{width:30,height:2,background:'#39c98a',marginTop:7,borderRadius:2}}/>
            </div>
          </div>
          {(()=>{
            const up=deltaCheckin>0,down=deltaCheckin<0;
            const col=up?'#1e8a5a':down?'#c47a00':'#9aa6ac';
            const bg=up?'#1fae7a':down?'#e08a1e':'#b7c0c5';
            const txt=deltaCheckin===0?'identique à la semaine passée':`${up?'+':''}${deltaCheckin} vs semaine passée`;
            // Flèche "stonks" : carré arrondi coloré + flèche brisée montante/descendante (ou trait stable).
            const path=up?'M5 15 L9 11 L12 13 L17 7':down?'M5 7 L9 11 L12 9 L17 15':'M5 11 L17 11';
            const head=up?<polyline points="17,7 17,11 13,9" fill="#fff"/>:down?<polyline points="17,15 17,11 13,13" fill="#fff"/>:null;
            return(<div style={{display:'flex',alignItems:'center',gap:9}}>
              <div style={{width:38,height:38,borderRadius:11,background:bg,boxShadow:'4px 4px 10px var(--nsh1),-3px -3px 8px var(--nsh2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <svg width="24" height="24" viewBox="0 0 22 22" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d={path}/>{head}
                </svg>
              </div>
              <span style={{fontSize:13,fontWeight:700,color:col}}>{txt}</span>
            </div>);
          })()}
        </div>
        <div className="card" style={{padding:16,textAlign:'center'}}>
          <div style={{fontSize:10,color:'var(--si)',textTransform:'uppercase',letterSpacing:'.05em'}}>Prestations semaine TTC</div>
          <div className="D" style={{fontSize:22,color:'#0f6e56',marginTop:2}}>{feur(montantSemaine)}</div>
          <div style={{fontSize:10,color:'var(--si)'}}>provisoire</div>
        </div>
      </div>
    </div>

    {/* Ligne 2 : derniers rapports avec photos */}
    <div className="card" style={{padding:20}}>
      <div className="D" style={{fontSize:16,marginBottom:14}}>📸 Derniers rapports de ménage</div>
      {derniersRapports.length===0?<div className="empty">Aucun rapport avec photo de salon ou chambre pour le moment.</div>:
      <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
        {derniersRapports.map(({r,img})=>{const b=myB.find(x=>x.id===r.bid);const n=nbPhotos(r);return(
          <div key={r.id} onClick={()=>onVoirRapport(r)} className="scard" style={{flex:1,minWidth:160,cursor:'pointer',overflow:'hidden',padding:0}}>
            <div style={{height:96,background:'var(--li)',display:'flex',alignItems:'center',justifyContent:'center'}}>
              <img src={img} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
            </div>
            <div style={{padding:'10px 12px'}}>
              <div style={{fontWeight:700,fontSize:13}}>{b?.name||'Logement'}</div>
              <div style={{fontSize:11,color:'var(--si)'}}>{fd(r.date)} · {n} photo{n>1?'s':''}</div>
            </div>
          </div>);})}
      </div>}
    </div>
  </div>);
}

// ── Planning propriétaire (clients prestations, lecture seule) ──
// Affiche UNIQUEMENT les ménages (full/quick) des biens du client, par semaine, avec l'heure de
// début. JAMAIS l'intervenant. Les ménages "à risque" (logement non programmé mais surveillé) sont
// montrés avec un badge neutre "Sous surveillance" — c'est volontaire : le propriétaire doit voir
// que La Conciergerie garde l'œil sur ces dates. Les chevauchements sont simplement listés.
function ClientPlanning({myB,st,als}){
  const bIds=new Set(myB.map(b=>b.id));
  const[ws,setWs]=useState(()=>new Date(lundiDeISO(iD(new Date()))+'T12:00:00'));
  const lundi=iD(ws), dimanche=iD(aD(ws,6));
  // Toutes les tâches ménage de la semaine (non annulées)
  const taches=(st||[])
    .filter(t=>bIds.has(t.bid)&&(t.type==='full'||t.type==='quick')&&t.st!=='cancelled'&&t.date>=lundi&&t.date<=dimanche)
    .sort((a,b)=>a.date.localeCompare(b.date)||(a.sh-b.sh)||(a.sm-b.sm));
  const jours=[0,1,2,3,4,5,6].map(i=>iD(aD(ws,i)));
  const hhmm=t=>`${String(t.sh).padStart(2,'0')}h${String(t.sm).padStart(2,'0')}`;
  const labelSem=`${new Date(lundi+'T12:00:00').toLocaleDateString('fr-FR',{day:'numeric',month:'short'})} → ${new Date(dimanche+'T12:00:00').toLocaleDateString('fr-FR',{day:'numeric',month:'short'})}`;
  const aujourdhui=iD(new Date());

  // Badge de statut pour un ménage programmé (pas sous surveillance)
  const BadgeStatut=({t})=>{
    const es=effSt(t,als);
    if(es==='done')return <span style={{fontSize:11,fontWeight:700,background:'#e8f5ee',color:'#0f6e56',border:'1px solid #a3d9bc',padding:'3px 10px',borderRadius:999}}>✓ Terminé</span>;
    if(es==='progress')return <span style={{fontSize:11,fontWeight:700,background:'#dbeeff',color:'#1a5fa0',border:'1px solid #93c5fd',padding:'3px 10px',borderRadius:999,animation:'pulse 2s infinite'}}>⟳ En cours</span>;
    return <span style={{fontSize:11,fontWeight:600,color:'#555',background:'#f1f1f0',border:'1px solid #ddd',padding:'3px 10px',borderRadius:999}}>Programmé</span>;
  };

  // Carte d'un ménage normal (programmé / en cours / terminé)
  const CarteNormale=({t})=>{
    const b=myB.find(x=>x.id===t.bid);
    const es=effSt(t,als);
    const borderColor=es==='done'?'#39c98a':es==='progress'?'#4a9eff':'#c5c5c0';
    return(
      <div className="card" style={{padding:'12px 16px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',borderTop:`3px solid ${borderColor}`,opacity:es==='done'?0.75:1}}>
        <div style={{fontSize:15,fontWeight:800,color:es==='progress'?'#1a5fa0':es==='done'?'#555':'#0f6e56',minWidth:62}}>{hhmm(t)}</div>
        <div style={{flex:1,minWidth:140}}>
          <div style={{fontWeight:700,fontSize:13}}>{b?.name||'Logement'}</div>
          <div style={{fontSize:11,color:'var(--si)'}}>{t.type==='quick'?'⚡ Ménage rapide':'🧹 Ménage complet'}</div>
        </div>
        <BadgeStatut t={t}/>
      </div>);
  };

  // Carte d'un ménage sous surveillance
  const CarteSurveillance=({t})=>{
    const b=myB.find(x=>x.id===t.bid);
    return(
      <div className="card" style={{padding:'10px 16px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',borderTop:'3px solid #e5a000',opacity:0.88}}>
        <div style={{fontSize:15,fontWeight:800,color:'#7a5a00',minWidth:62}}>—</div>
        <div style={{flex:1,minWidth:140}}>
          <div style={{fontWeight:700,fontSize:13,color:'#7a5a00'}}>{b?.name||'Logement'}</div>
          <div style={{fontSize:11,color:'var(--si)'}}>{t.type==='quick'?'⚡ Ménage rapide':'🧹 Ménage complet'}</div>
        </div>
        <span style={{fontSize:11,fontWeight:700,background:'#fff4d6',color:'#7a5a00',border:'1px solid #e5c977',padding:'3px 10px',borderRadius:999}}>🔍 Sous surveillance</span>
      </div>);
  };

  return(<div>
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
      <div className="D" style={{fontSize:18}}>📅 Planning des ménages</div>
      <div style={{display:'flex',alignItems:'center',gap:6}}>
        <button onClick={()=>setWs(aD(ws,-7))} className="btn bgh sm">‹</button>
        <span style={{fontSize:13,fontWeight:700,minWidth:140,textAlign:'center'}}>{labelSem}</span>
        <button onClick={()=>setWs(aD(ws,7))} className="btn bgh sm">›</button>
        <button onClick={()=>setWs(new Date(lundiDeISO(aujourdhui)+'T12:00:00'))} className="btn bgh sm" style={{fontSize:11}}>Cette semaine</button>
      </div>
    </div>
    {taches.length===0?<div className="empty">Aucun ménage planifié cette semaine.</div>:
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      {jours.map((j,i)=>{
        const tjTous=taches.filter(t=>t.date===j);
        if(tjTous.length===0)return null;
        const estAujourdhui=j===aujourdhui;
        // Séparer ménages normaux (affectés) et sous surveillance (risque sans affectation)
        const tjNormaux=tjTous.filter(t=>!(t.risque&&!t.sid));
        const tjSurv=tjTous.filter(t=>t.risque&&!t.sid);
        return(
          <div key={j}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:7}}>
              <span style={{fontWeight:700,fontSize:13,color:estAujourdhui?'#0f6e56':'#333'}}>{JOUR_ASCII[i]} {new Date(j+'T12:00:00').toLocaleDateString('fr-FR',{day:'numeric',month:'long'})}</span>
              {estAujourdhui&&<span style={{fontSize:10,fontWeight:700,background:'linear-gradient(135deg,#39e8a4,#1fae7a)',color:'#06291c',padding:'1px 8px',borderRadius:999}}>Aujourd'hui</span>}
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {/* Ménages programmés / en cours / terminés en premier */}
              {tjNormaux.map(t=><CarteNormale key={t.id} t={t}/>)}
              {/* Ménages sous surveillance regroupés en bas, séparés visuellement */}
              {tjSurv.length>0&&(
                <div style={{marginTop:tjNormaux.length>0?6:0}}>
                  {tjNormaux.length>0&&<div style={{fontSize:10,fontWeight:700,letterSpacing:'.06em',textTransform:'uppercase',color:'#b47d00',marginBottom:5,paddingLeft:2}}>Sous surveillance</div>}
                  <div style={{display:'flex',flexDirection:'column',gap:6}}>
                    {tjSurv.map(t=><CarteSurveillance key={t.id} t={t}/>)}
                  </div>
                </div>
              )}
            </div>
          </div>);})}
    </div>}
    {taches.some(t=>t.risque&&!t.sid)&&(
      <div style={{fontSize:11,color:'var(--si)',marginTop:14,lineHeight:1.6,display:'flex',gap:8}}>
        <span>🔍</span>
        <span><strong>Sous surveillance</strong> : un logement dont le ménage n'est pas encore calé à une heure précise, mais que La Conciergerie garde à l'œil. Aucune action de votre part n'est nécessaire.</span>
      </div>
    )}
  </div>);
}

// ── Guide propriétaire (contenu de la modale aide) ──
function GuideProprietaire({isPresta,planningOn}){
  const tabsAll=[
    {id:'dash',label:'📊 Tableau de bord',presta:true},
    {id:'recap',label:'📊 Récap locations',presta:false},
    {id:'ts',label:'🏛️ Taxe de séjour',presta:false},
    {id:'dem',label:'📨 Demandes',presta:'both'},
    {id:'reclam',label:'🛠 Réclamations',presta:true},
    {id:'plan',label:'📅 Planning',presta:'planning'},
    {id:'fac',label:'🧾 Factures',presta:'both'},
    {id:'rap',label:'📋 Rapports',presta:'both'},
    {id:'bien',label:'🏠 Mes biens',presta:'both'},
    {id:'msg',label:'💬 Messagerie',presta:'both'},
  ];
  const tabs2=tabsAll.filter(t=>{
    if(t.presta==='both')return true;
    if(t.presta==='planning')return isPresta&&planningOn;
    return isPresta?t.presta===true:t.presta===false;
  });
  const[gt,setGt]=useState(isPresta?'dash':'recap');
  return(<div style={{fontFamily:'inherit'}}>
    {/* Onglets du guide */}
    <div style={{display:'flex',gap:5,flexWrap:'wrap',marginBottom:16}}>
      {tabs2.map(t=><button key={t.id} onClick={()=>setGt(t.id)} style={{fontSize:11,padding:'5px 11px',borderRadius:20,border:`1.5px solid ${gt===t.id?'var(--pk)':'#ddd'}`,background:gt===t.id?'var(--pk)':'#fff',color:gt===t.id?'#fff':'#666',cursor:'pointer',fontWeight:gt===t.id?700:400}}>{t.label}</button>)}
    </div>

    {/* ── TABLEAU DE BORD (prestations) ── */}
    {gt==='dash'&&<div>
      <div style={{background:'linear-gradient(135deg,#e1f7ee,#f5fffb)',borderRadius:12,padding:'12px 16px',marginBottom:14,border:'1px solid #b8e8d4'}}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>📊 Tableau de bord</div>
        <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>Votre page d'accueil. En un coup d'œil : les ménages de la semaine en cours, le nombre de check-in, le montant provisoire des prestations et vos derniers rapports de ménage avec photos.</div>
      </div>
      <GuideStep num="1" title="Ménages de la semaine" sub="Le prochain créneau est mis en avant">
        <div style={{fontSize:12,color:'#666',lineHeight:1.6,marginTop:6}}>Tous les ménages programmés du lundi au dimanche, avec l'heure de début. Les ménages déjà réalisés apparaissent estompés avec une coche.</div>
      </GuideStep>
      <GuideStep num="2" title="Check-in & prestations" sub="Indicateurs de la semaine">
        <div style={{fontSize:12,color:'#666',lineHeight:1.6,marginTop:6}}>Le cercle indique le nombre de check-in de la semaine. Le montant « prestations semaine » est un total provisoire, susceptible d'évoluer jusqu'à l'émission de la facture.</div>
      </GuideStep>
      <GuideStep num="3" title="Derniers rapports" sub="Avec photos">
        <div style={{fontSize:12,color:'#666',lineHeight:1.6,marginTop:6}}>Vos 3 derniers rapports de ménage avec photos. Cliquez sur une vignette pour ouvrir le rapport complet.</div>
      </GuideStep>
    </div>}

    {/* ── RÉCAP LOCATIONS ── */}
    {gt==='recap'&&<div>
      <div style={{background:'linear-gradient(135deg,#ffe8e8,#fff8f5)',borderRadius:12,padding:'12px 16px',marginBottom:14,border:'1px solid #f5d8d0'}}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>📊 Récap locations</div>
        <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>Votre tableau de bord financier. Retrouvez toutes vos locations avec le détail des versements, prestations et frais de services.</div>
      </div>
      <GuideStep num="1" title="Choisir la période" sub="Mois · Année · Plage libre">
        <div style={{display:'flex',gap:6,flexWrap:'wrap',margin:'10px 0 6px'}}>
          {['Mois','Année','Plage perso'].map((l,i)=><span key={l} style={{fontSize:11,padding:'4px 12px',borderRadius:20,border:`1.5px solid ${i===1?'var(--pk)':'#ddd'}`,background:i===1?'var(--pk)':'#fff',color:i===1?'#fff':'#666',fontWeight:i===1?700:400}}>{l}</span>)}
          <select style={{fontSize:11,border:'1.5px solid #ddd',borderRadius:8,padding:'3px 8px'}}><option>2025</option><option>2026</option></select>
        </div>
        <GuideTip>Utilisez <strong>Plage perso</strong> pour afficher n'importe quelle période (ex&nbsp;: un trimestre fiscal).</GuideTip>
      </GuideStep>
      <GuideStep num="2" title="Les 4 indicateurs clés" sub="En haut du tableau">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,margin:'10px 0'}}>
          {[['🌙 Nuitées louées','25','#333'],['Versé plateformes','4 879 €','#4a9e6b'],['Frais conciergerie TTC','1 795 €','#e8846a'],['Revenu net','3 084 €','#e87c2a']].map(([l,v,c])=><div key={l} style={{border:'1.5px solid #e8ddd4',borderRadius:10,padding:'9px 12px'}}><div style={{fontSize:10,color:'#888',marginBottom:3}}>{l}</div><div style={{fontSize:17,fontWeight:700,color:c}}>{v}</div></div>)}
        </div>
        <GuideTip><strong>Versé (+)</strong> = ce que Booking/Airbnb vous reverse (leur commission déjà déduite). <strong>Net (=)</strong> = ce qui vous revient après déduction des frais de La Conciergerie.</GuideTip>
      </GuideStep>
      <GuideStep num="3" title="Détail par séjour" sub="Cliquez sur ▶ pour déplier">
        <div style={{margin:'10px 0',border:'1px solid #e8ddd4',borderRadius:10,overflow:'hidden',fontSize:11}}>
          <div style={{background:'#1a1a1a',color:'#fff',display:'grid',gridTemplateColumns:'20px 1fr 80px 60px 70px 70px',gap:6,padding:'7px 10px',fontWeight:600,fontSize:10}}>
            <span></span><span>BIEN / LOCATAIRE</span><span>DATES</span><span>OTA</span><span>VERSÉ</span><span>NET</span>
          </div>
          {[['Les hauts de Colmar','Alice M. (FR)','03/04→06/04','BOOKING','209 €','122 €'],['Les hauts de Colmar','Thomas B. (DE)','15/06→17/06','AIRBNB','288 €','193 €']].map(([b,l,d,p,v,n])=><div key={l} style={{display:'grid',gridTemplateColumns:'20px 1fr 80px 60px 70px 70px',gap:6,padding:'7px 10px',borderTop:'1px solid #f0e8e0',alignItems:'center'}}>
            <span style={{color:'#aaa'}}>▶</span>
            <div><div style={{fontWeight:600,fontSize:11}}>{b}</div><div style={{fontSize:10,color:'#888'}}>{l}</div></div>
            <span style={{fontSize:10,color:'#888'}}>{d}</span>
            <span style={{background:p==='BOOKING'?'#003580':'#ff5a5f',color:'#fff',fontSize:9,padding:'2px 5px',borderRadius:4,fontWeight:700}}>{p}</span>
            <span style={{color:'#4a9e6b',fontWeight:600}}>{v}</span>
            <span style={{color:'#e87c2a',fontWeight:600}}>{n}</span>
          </div>)}
        </div>
        <GuideTip>Le bouton <strong>▶</strong> révèle le détail complet&nbsp;: paiement locataire, commission plateforme, taxe de séjour, nombre d'adultes et d'enfants.</GuideTip>
      </GuideStep>
    </div>}

    {/* ── TAXE DE SÉJOUR ── */}
    {gt==='ts'&&<div>
      <div style={{background:'linear-gradient(135deg,#eef6ff,#f8fbff)',borderRadius:12,padding:'12px 16px',marginBottom:14,border:'1px solid #cfe3ff'}}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>🏛️ Taxe de séjour</div>
        <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>Un tableau mensuel pour vous aider à remplir votre déclaration de taxe de séjour en mairie, séjour par séjour.</div>
      </div>
      <GuideStep num="1" title="Choisir le mois" sub="Sélecteur en haut">
        <GuideTip>Chaque séjour est rattaché au mois de sa <strong>date d'arrivée</strong>. Vous pouvez aussi filtrer sur un seul bien.</GuideTip>
      </GuideStep>
      <GuideStep num="2" title="Lire la colonne « Statut taxe »" sub="Le point le plus important">
        <div style={{display:'flex',flexDirection:'column',gap:6,margin:'10px 0'}}>
          <div style={{fontSize:11,padding:'6px 10px',borderRadius:8,background:'#eef6ff',border:'1px solid #cfe3ff'}}><strong style={{color:'#2563eb'}}>Prélevée par Booking / Airbnb…</strong> — la plateforme a déjà collecté la taxe et la reverse elle-même à la commune. Rien à faire de votre côté.</div>
          <div style={{fontSize:11,padding:'6px 10px',borderRadius:8,background:'#fff4e0',border:'1px solid #f5d9a0'}}><strong style={{color:'#e87c2a'}}>À régler (arrivée)</strong> — la taxe a été perçue à l'arrivée du voyageur&nbsp;; c'est à vous de la reverser à la commune lors de votre déclaration.</div>
        </div>
        <GuideTip>Le bas du tableau récapitule le total à reverser pour le mois.</GuideTip>
      </GuideStep>
      <GuideStep num="3" title="Exporter" sub="Bouton « Export CSV »">
        <GuideTip>Le fichier CSV s'ouvre dans Excel ou Numbers&nbsp;: pratique pour recopier les chiffres dans le formulaire en ligne de votre mairie.</GuideTip>
      </GuideStep>
    </div>}

    {/* ── FACTURES ── */}
    {gt==='fac'&&<div>
      <div style={{background:'linear-gradient(135deg,#e8f5ee,#f5fff8)',borderRadius:12,padding:'12px 16px',marginBottom:14,border:'1px solid #c8e8d4'}}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>🧾 Factures</div>
        <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>Retrouvez ici toutes les factures émises par La Conciergerie. Téléchargez les PDF et suivez leur statut de règlement.</div>
      </div>
      <GuideStep num="1" title="Lire une facture" sub="Numéro · période · montant · statut">
        <div style={{border:'1px solid #e8ddd4',borderRadius:12,padding:'14px 16px',margin:'10px 0',display:'flex',alignItems:'center',gap:14,flexWrap:'wrap',borderLeft:'4px solid #4a9e6b'}}>
          <span style={{fontSize:28}}>🧾</span>
          <div style={{flex:1,minWidth:160}}>
            <div style={{color:'#e8846a',fontWeight:700,fontSize:14}}>FC-2026-004</div>
            <div style={{fontSize:12,color:'#888',margin:'2px 0'}}>Avril 2026</div>
            <div style={{fontSize:11,color:'#aaa'}}>Émise le 01/06/2026</div>
            <span style={{background:'#eaf5ee',color:'#4a9e6b',fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:10,display:'inline-block',marginTop:4}}>✅ Réglée</span>
          </div>
          <div style={{textAlign:'right'}}>
            <div style={{fontSize:10,color:'#aaa',textTransform:'uppercase',letterSpacing:'.05em'}}>Total TTC</div>
            <div style={{fontWeight:800,fontSize:20,color:'#4a9e6b'}}>142,80 €</div>
            <button style={{background:'#e8846a',color:'#fff',border:'none',borderRadius:20,padding:'5px 12px',fontSize:11,fontWeight:600,marginTop:5,cursor:'pointer'}}>⬇ PDF</button>
          </div>
        </div>
        <GuideTip>Le badge <strong>Réglée</strong> s'affiche dès que La Conciergerie confirme la réception de votre paiement. Les brouillons ne sont <strong>jamais</strong> visibles depuis votre espace.</GuideTip>
      </GuideStep>
      <GuideStep num="2" title="Télécharger le PDF" sub="Format Factur-X standard">
        <GuideTip>Cliquez sur le bouton <strong>⬇ PDF</strong> pour télécharger la facture. Ce PDF est au format Factur-X, reconnu par les logiciels comptables. Conservez-le pour votre comptable.</GuideTip>
      </GuideStep>
    </div>}

    {/* ── RAPPORTS ── */}
    {gt==='rap'&&<div>
      <div style={{background:'linear-gradient(135deg,#e8f1fb,#f5f8ff)',borderRadius:12,padding:'12px 16px',marginBottom:14,border:'1px solid #c8d8f0'}}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>📋 Rapports de ménage</div>
        <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>Après chaque intervention, l'équipe soumet un rapport avec checklist et photos. Il apparaît ici en temps réel.</div>
      </div>
      <GuideStep num="1" title="Consulter un rapport" sub="Checklist + photos avant/après">
        <div style={{border:'1px solid #e8ddd4',borderRadius:12,overflow:'hidden',margin:'10px 0'}}>
          <div style={{background:'#f5ede3',padding:'9px 14px',display:'flex',alignItems:'center',gap:8}}>
            <span>🏠</span><span style={{fontWeight:700,fontSize:13,flex:1}}>Les hauts de Colmar</span><span style={{fontSize:11,color:'#888'}}>02/06/2026 · 10h15</span>
          </div>
          <div style={{padding:'10px 14px',display:'flex',gap:10,flexWrap:'wrap'}}>
            <div style={{display:'flex',gap:6}}>
              {['#d4e8c2','#c2d4e8'].map((c,i)=><div key={i} style={{width:52,height:44,borderRadius:8,background:c,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>📷</div>)}
            </div>
            <div>
              {['Cuisine nettoyée','Salle de bain','Linge changé','Poubelles vidées'].map(item=><div key={item} style={{fontSize:11,display:'flex',alignItems:'center',gap:5,marginBottom:3}}><span style={{color:'#4a9e6b',fontWeight:700}}>✓</span>{item}</div>)}
            </div>
          </div>
        </div>
        <GuideTip>Cliquez sur <strong>Voir</strong> pour ouvrir le rapport complet avec toutes les photos, ou <strong>PDF</strong> pour le télécharger.</GuideTip>
      </GuideStep>
      <GuideStep num="2" title="Masquer un rapport" sub="Sans suppression définitive">
        <GuideTip>Le bouton 🗑 retire le rapport de votre vue. La Conciergerie le conserve dans ses archives — l'action est réversible sur demande.</GuideTip>
      </GuideStep>
    </div>}

    {/* ── MES BIENS ── */}
    {gt==='bien'&&<div>
      <div style={{background:'linear-gradient(135deg,#f0ebfe,#faf8ff)',borderRadius:12,padding:'12px 16px',marginBottom:14,border:'1px solid #ddd0f8'}}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>🏠 Mes biens</div>
        <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>La fiche de chaque appartement géré pour vous par La Conciergerie.</div>
      </div>
      <div style={{border:'1.5px solid #f0a898',borderRadius:12,padding:'14px 16px',marginBottom:12}}>
        <div style={{fontWeight:700,fontSize:15,marginBottom:3}}>Les hauts de Colmar</div>
        <div style={{fontSize:12,color:'#888'}}>12 rue des artisans, 68000 Colmar</div>
      </div>
      <GuideTip>Pour modifier les informations de votre bien (capacité, équipements, code d'accès…), contactez l'équipe via l'onglet <strong>Messagerie</strong>.</GuideTip>
    </div>}

    {/* ── DEMANDES ── */}
    {gt==='dem'&&<div>
      <div style={{background:'linear-gradient(135deg,#fff4e8,#fffaf5)',borderRadius:12,padding:'12px 16px',marginBottom:14,border:'1px solid #f5d8b0'}}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>📨 Demandes</div>
        <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>Faites une demande ponctuelle à l'équipe et suivez son avancement en temps réel. Types disponibles&nbsp;: Ménage, Check-in, Travaux, Linge, Autre.</div>
      </div>
      <GuideStep num="1" title="Demande de ménage" sub="Avec créneau horaire optionnel">
        <div style={{background:'#faf6f0',border:'1px solid #e8ddd4',borderRadius:12,padding:'14px',margin:'10px 0',fontSize:12}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10}}>
            <div><div style={{fontSize:10,fontWeight:700,color:'#888',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3}}>Type</div><div style={{border:'1.5px solid #e0d6cc',borderRadius:8,padding:'7px 10px',background:'#fff'}}>Ménage ▾</div></div>
            <div><div style={{fontSize:10,fontWeight:700,color:'#888',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3}}>Date souhaitée</div><div style={{border:'1.5px solid #e0d6cc',borderRadius:8,padding:'7px 10px',background:'#fff',color:'#aaa'}}>08/06/2026 📅</div></div>
          </div>
          <div style={{fontSize:10,fontWeight:700,color:'#888',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:6}}>Créneau horaire</div>
          <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
            {[['✓ N\'importe quand',true],['Disponible à partir de…',false],['À faire avant…',false]].map(([l,a])=>
              <span key={l} style={{fontSize:11,padding:'4px 10px',borderRadius:20,border:`1.5px solid ${a?'#e8846a':'#ddd'}`,background:a?'#fde8e2':'#fff',color:a?'#c0392b':'#666',fontWeight:a?700:400}}>{l}</span>
            )}
          </div>
        </div>
        <GuideTip>Si le logement est occupé en début de journée, précisez <strong>Disponible à partir de…</strong> et choisissez l'heure. Si un locataire arrive en soirée, utilisez <strong>À faire avant…</strong>.</GuideTip>
      </GuideStep>
      <GuideStep num="2" title="Demande de check-in" sub="Coordonnées du locataire à transmettre">
        <div style={{background:'#faf6f0',border:'1px solid #e8ddd4',borderRadius:12,padding:'14px',margin:'10px 0',fontSize:12}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10}}>
            <div><div style={{fontSize:10,fontWeight:700,color:'#888',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3}}>Type</div><div style={{border:'1.5px solid #e0d6cc',borderRadius:8,padding:'7px 10px',background:'#fff'}}>Check-in ▾</div></div>
            <div><div style={{fontSize:10,fontWeight:700,color:'#888',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3}}>Date d'arrivée</div><div style={{border:'1.5px solid #e0d6cc',borderRadius:8,padding:'7px 10px',background:'#fff',color:'#aaa'}}>08/06/2026 📅</div></div>
          </div>
          <div style={{border:'1px solid #e0d6cc',borderRadius:8,padding:'10px',background:'#fff'}}>
            <div style={{fontSize:10,fontWeight:700,color:'#888',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:6}}>Coordonnées du locataire</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:6}}>
              <div style={{background:'#f5f5f5',borderRadius:6,padding:'6px 8px',color:'#aaa'}}>Nom du locataire</div>
              <div style={{background:'#f5f5f5',borderRadius:6,padding:'6px 8px',color:'#aaa'}}>+33 6 00 00 00 00</div>
            </div>
            <div style={{background:'#f5f5f5',borderRadius:6,padding:'6px 8px',color:'#aaa'}}>email@exemple.com</div>
          </div>
        </div>
        <GuideTip>Ces coordonnées sont transmises <strong>uniquement</strong> à l'équipe pour organiser l'accueil — elles ne sont pas stockées dans votre récap locations.</GuideTip>
      </GuideStep>
      <GuideStep num="3" title="Suivre vos demandes" sub="Statut en temps réel">
        <div style={{display:'flex',gap:6,flexWrap:'wrap',margin:'10px 0'}}>
          {[['En attente','#f5d8b0','#8b5e00'],['Acceptée','#d4f0e0','#2e7d52'],['Refusée','#fde8e2','#c0392b']].map(([l,bg,c])=>
            <span key={l} style={{background:bg,color:c,fontSize:11,fontWeight:700,padding:'4px 12px',borderRadius:20}}>{l}</span>
          )}
        </div>
        <GuideTip>L'équipe met à jour le statut de chaque demande. Vous pouvez aussi laisser une description dans le champ libre pour préciser vos besoins.</GuideTip>
      </GuideStep>
    </div>}

    {/* ── MESSAGERIE ── */}
    {gt==='msg'&&<div>
      <div style={{background:'linear-gradient(135deg,#f3e8fe,#fdf8ff)',borderRadius:12,padding:'12px 16px',marginBottom:14,border:'1px solid #e0c8f8'}}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>💬 Messagerie</div>
        <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>Échangez directement avec l'équipe La Conciergerie.</div>
      </div>
      <GuideStep num="1" title="Envoyer un message" sub="Questions et demandes de suivi">
        <div style={{border:'1.5px solid #e8ddd4',borderRadius:12,overflow:'hidden',margin:'10px 0'}}>
          <div style={{background:'#f5ede3',padding:'8px 14px',fontWeight:600,fontSize:13,borderBottom:'1px solid #e8ddd4'}}>La Conciergerie</div>
          <div style={{padding:'16px',textAlign:'center',color:'#aaa',fontSize:12,fontStyle:'italic'}}>Aucun message</div>
          <div style={{display:'flex',gap:8,padding:'10px 12px',borderTop:'1px solid #e8ddd4'}}>
            <div style={{flex:1,border:'1px solid #e8ddd4',borderRadius:20,padding:'7px 14px',fontSize:12,color:'#aaa',background:'#faf6f0'}}>Ton message…</div>
            <div style={{width:34,height:34,borderRadius:'50%',background:'#f0a898',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:14}}>➤</div>
          </div>
        </div>
        <GuideTip>Le badge rouge sur l'onglet <strong>Messagerie</strong> signale les messages non lus. Les questions simples reçoivent une réponse rapide ; les sujets complexes sont transmis directement à Claire ou Franck.</GuideTip>
      </GuideStep>
    </div>}

    {/* ── RÉCLAMATIONS ── */}
    {gt==='reclam'&&<div>
      <div style={{background:'linear-gradient(135deg,#fde8e8,#fff5f5)',borderRadius:12,padding:'12px 16px',marginBottom:14,border:'1px solid #f5c0c0'}}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>🛠 Réclamations</div>
        <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>Un espace dédié pour signaler un problème : qualité d'une intervention, oubli, dégradation constatée, anomalie. Distinct des demandes courantes pour un meilleur suivi.</div>
      </div>
      <GuideStep num="1" title="Signaler un problème" sub="Bouton « Réclamation »">
        <GuideTip>Choisissez le logement concerné, la date du problème, et décrivez précisément ce que vous avez constaté. Plus c'est détaillé, plus vite l'équipe peut agir.</GuideTip>
      </GuideStep>
      <GuideStep num="2" title="Suivre le traitement" sub="Statut en temps réel">
        <div style={{display:'flex',gap:6,flexWrap:'wrap',margin:'10px 0'}}>
          {[['En cours de traitement','#f5d8b0','#8b5e00'],['Traitée','#d4f0e0','#2e7d52'],['Clôturée','#eee','#666']].map(([l,bg,c])=>
            <span key={l} style={{background:bg,color:c,fontSize:11,fontWeight:700,padding:'4px 12px',borderRadius:20}}>{l}</span>
          )}
        </div>
        <GuideTip>L'équipe est notifiée dès l'envoi et met à jour le statut au fur et à mesure. Vous pouvez compléter via la <strong>Messagerie</strong> si besoin.</GuideTip>
      </GuideStep>
    </div>}

    {/* ── PLANNING ── */}
    {gt==='plan'&&<div>
      <div style={{background:'linear-gradient(135deg,#fde8f0,#fff5fa)',borderRadius:12,padding:'12px 16px',marginBottom:14,border:'1px solid #f5c0d8'}}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>📅 Planning des ménages</div>
        <div style={{fontSize:12,color:'#666',lineHeight:1.6}}>Une vue semaine par semaine des ménages planifiés pour vos logements, avec l'heure de début d'intervention.</div>
      </div>
      <GuideStep num="1" title="Naviguer dans les semaines" sub="Flèches ‹ ›">
        <GuideTip>Utilisez les flèches pour avancer ou revenir en arrière, et <strong>Cette semaine</strong> pour revenir au présent. Seuls vos ménages apparaissent, jamais ceux des autres propriétaires.</GuideTip>
      </GuideStep>
      <GuideStep num="2" title="Lire une ligne" sub="Heure de début · logement · type">
        <GuideTip>L'heure affichée est l'heure de <strong>début</strong> prévue. Plusieurs ménages peuvent se chevaucher : c'est normal, l'équipe s'organise en conséquence. Le nom de l'intervenant n'est pas affiché.</GuideTip>
      </GuideStep>
      <GuideStep num="3" title="« Sous surveillance »" sub="Logement non encore programmé">
        <GuideTip>Un logement <strong>Sous surveillance</strong> n'a pas encore de ménage calé à une date précise, mais La Conciergerie le garde à l'œil. Aucune action de votre part n'est nécessaire.</GuideTip>
      </GuideStep>
    </div>}
  </div>);
}

function GuideStep({num,title,sub,children}){
  const[open,setOpen]=useState(false);
  return(<div style={{border:'1px solid #e8ddd4',borderRadius:12,marginBottom:10,overflow:'hidden'}}>
    <div onClick={()=>setOpen(o=>!o)} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',cursor:'pointer',background:open?'#fff8f5':'#fff'}}>
      <div style={{width:28,height:28,borderRadius:'50%',background:'var(--pk)',color:'#fff',fontWeight:700,fontSize:13,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{num}</div>
      <div style={{flex:1}}><div style={{fontWeight:700,fontSize:13}}>{title}</div><div style={{fontSize:11,color:'#888'}}>{sub}</div></div>
      <span style={{fontSize:18,color:'#aaa',transform:open?'rotate(90deg)':'none',transition:'transform .2s'}}>›</span>
    </div>
    {open&&<div style={{padding:'0 14px 14px',borderTop:'1px solid #f5ede3'}}>{children}</div>}
  </div>);
}

function GuideTip({children}){
  return(<div style={{background:'#faf6f0',borderRadius:9,padding:'8px 12px',fontSize:11,color:'#666',lineHeight:1.6,display:'flex',gap:8,marginTop:4}}>
    <span style={{flexShrink:0}}>💡</span><span>{children}</span>
  </div>);
}

export { ClientOnboarding, CaniculeBannerClient, MigrationBanner, ClientUI, DemandeForm, ClientDashboardPresta, ClientPlanning, GuideProprietaire, GuideStep, GuideTip };
